import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { resolveColumnMeta } from "../columns/columnKinds";
import type {
    DataGridColumnInstance,
    DataGridPin,
    DataGridTable,
} from "../columns/types";
import { getColumnStyle } from "../layout/columnVars";
import { SKELETON_ROW_COUNT } from "../layout/density";
import gridStyles from "../grid/Grid.module.scss";
import { SkeletonShape } from "./SkeletonShape";
import styles from "./states.module.scss";

interface SkeletonRowsProps<TData extends RowData> {
    table: DataGridTable<TData>;
    /** Defaults to `SKELETON_ROW_COUNT`. */
    rowCount?: number;
    className?: string;
}

const toRowIndexes = (rowCount: number) =>
    Array.from({ length: rowCount }, (_, index) => index);

const renderCells = <TData extends RowData>({
    rowIndex,
    columns,
    pin,
}: {
    rowIndex: number;
    columns: ReadonlyArray<DataGridColumnInstance<TData>>;
    pin: DataGridPin | false;
}) =>
    columns.map((column, columnIndex) => {
        const meta = resolveColumnMeta(column.columnDef, column.id);
        return (
            <div
                key={column.id}
                className={gridStyles.cell}
                style={getColumnStyle(column.id, pin)}
                data-kind={meta.kind}
                data-align={meta.align}
                data-pin={pin || undefined}
            >
                <SkeletonShape
                    skeleton={meta.skeleton}
                    isTree={meta.tree}
                    rowIndex={rowIndex}
                    columnIndex={columnIndex}
                />
            </div>
        );
    });

/**
 * Placeholder rows at the current density, cells shaped by the real column
 * widths and pins, each drawing its column's skeleton (the kind's at-rest
 * look): first load, load more, and under a gate. Hidden from assistive
 * tech; the grid carries `aria-busy`.
 */
export const SkeletonRows = <TData extends RowData>({
    table,
    rowCount = SKELETON_ROW_COUNT,
    className,
}: Readonly<SkeletonRowsProps<TData>>) => {
    const startColumns = table.getStartVisibleLeafColumns();
    const centerColumns = table.getCenterVisibleLeafColumns();
    const endColumns = table.getEndVisibleLeafColumns();
    return (
        <div
            className={classNames(styles.skeletonBody, className)}
            aria-hidden
            data-testid="data-grid-skeleton"
        >
            {toRowIndexes(rowCount).map((rowIndex) => (
                <div key={rowIndex} className={styles.skeletonRow}>
                    {renderCells({
                        rowIndex,
                        columns: startColumns,
                        pin: "start",
                    })}
                    {renderCells({
                        rowIndex,
                        columns: centerColumns,
                        pin: false,
                    })}
                    <div className={gridStyles.filler} />
                    {renderCells({ rowIndex, columns: endColumns, pin: "end" })}
                </div>
            ))}
        </div>
    );
};
